import React, { useState, useRef, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useSearchState } from '../../contexts/SearchStateContext';
import { useTheme } from '../../hooks/useTheme';
import './AuthBar.css';

function UserIcon() {
  return (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <circle cx="12" cy="8" r="3" />
      <path d="M5 20v-2a5 5 0 0 1 5-5h4a5 5 0 0 1 5 5v2" />
    </svg>
  );
}

function ThemeToggle({ isDark, toggleTheme }) {
  return (
    <button
      type="button"
      className="auth-bar__theme-toggle"
      onClick={toggleTheme}
      aria-label={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
      title={isDark ? 'Light mode' : 'Dark mode'}
    >
      <span className="auth-bar__theme-icon" aria-hidden="true">
        {isDark ? '☀️' : '🌙'}
      </span>
    </button>
  );
}

function AuthSlot({ isSignedIn, signOut, setDropdownOpen, dropdownOpen, location, searchState, containerRef }) {
  return (
    <>
      {isSignedIn ? (
        <>
          <button
            type="button"
            className="auth-bar__trigger"
            onClick={() => setDropdownOpen((o) => !o)}
            aria-expanded={dropdownOpen}
            aria-haspopup="true"
            aria-label="Account menu"
          >
            <UserIcon />
          </button>
          {dropdownOpen && (
            <div className="auth-bar__dropdown" role="menu">
<p className="auth-bar__dropdown-status">You are signed in</p>
              <Link
                to="/account"
                className="auth-bar__dropdown-link"
                role="menuitem"
                state={{
                  from: location,
                  workingAddress: searchState.workingAddress ?? '',
                  stayingAddress: searchState.stayingAddress ?? '',
                }}
                onClick={() => setDropdownOpen(false)}
              >
                Account
              </Link>
              <Link
                to="/profile"
                className="auth-bar__dropdown-link"
                role="menuitem"
                state={{
                  from: location,
                  workingAddress: searchState.workingAddress ?? '',
                  stayingAddress: searchState.stayingAddress ?? '',
                }}
                onClick={() => setDropdownOpen(false)}
              >
                Profile
              </Link>
              <button
                type="button"
                className="auth-bar__dropdown-btn"
                role="menuitem"
                onClick={() => {
                  signOut();
                  setDropdownOpen(false);
                }}
              >
                Sign out
              </button>
            </div>
          )}
        </>
      ) : (
        <Link
          to="/signin"
          className="auth-bar__signin-link"
          state={{ from: location }}
        >
          Sign in
        </Link>
      )}
    </>
  );
}

export default function AuthBar({ variant, children }) {
  const { loading, signOut, isSignedIn } = useAuth();
  const location = useLocation();
  const { searchState } = useSearchState();
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const containerRef = useRef(null);
  const { isDark, toggle: toggleTheme } = useTheme();

  useEffect(() => {
    if (!dropdownOpen) return;

    function handleClickOutside(e) {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setDropdownOpen(false);
      }
    }

    function handleEscape(e) {
      if (e.key === 'Escape') {
        setDropdownOpen(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [dropdownOpen]);

  if (loading) {
    return (
      <div className="auth-bar auth-bar--loading" aria-hidden="true">
        <span className="auth-bar__loading">Loading…</span>
      </div>
    );
  }

  if (variant === 'banner') {
    return (
      <div className="auth-bar auth-bar--banner" ref={containerRef} role="region" aria-label="Account">
        <div className="auth-bar__left">
          <ThemeToggle isDark={isDark} toggleTheme={toggleTheme} />
        </div>
        <div className="auth-bar__center">
          {children}
        </div>
        <div className="auth-bar__right">
          <AuthSlot
            isSignedIn={isSignedIn}
            signOut={signOut}
            setDropdownOpen={setDropdownOpen}
            dropdownOpen={dropdownOpen}
            location={location}
            searchState={searchState}
            containerRef={containerRef}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="auth-bar" ref={containerRef} role="region" aria-label="Account">
      <ThemeToggle isDark={isDark} toggleTheme={toggleTheme} />
      <AuthSlot
        isSignedIn={isSignedIn}
        signOut={signOut}
        setDropdownOpen={setDropdownOpen}
        dropdownOpen={dropdownOpen}
        location={location}
        searchState={searchState}
        containerRef={containerRef}
      />
    </div>
  );
}
